import { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FioriPageHeader } from "@/components/fiori";
import { 
  Building2, 
  User, 
  FileText, 
  Shield, 
  CheckCircle,
  ArrowLeft,
  Save,
  Search,
  Plus,
  Printer,
  RotateCcw,
  Send,
  Loader2,
  Upload,
  X,
  CreditCard
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { cn } from "@/lib/utils";
import CountrySelect from "@/components/CountrySelect";

export default function NewCardRequest() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [currentSection, setCurrentSection] = useState("company");

  // Section definitions — built inside component so t() is available
  const SECTIONS = useMemo(() => [
    { id: "company",   code: "company",   name: t("mcm.sections.company",   "Company Information"),  icon: Building2 },
    { id: "personal",  code: "personal",  name: t("mcm.sections.personal",  "Personal Information"), icon: User },
    { id: "documents", code: "documents", name: t("mcm.sections.documents", "Documents"),             icon: FileText },
    { id: "access",    code: "access",    name: t("mcm.sections.access",    "Access Levels"),         icon: Shield },
    { id: "review",    code: "review",    name: t("mcm.sections.review",    "Review & Submit"),       icon: CheckCircle },
  ], [t]);

  // Form state
  const [formData, setFormData] = useState({
    companyType: "" as "contractor" | "subcontractor" | "client" | "permit3" | "internal" | "",
    companyId: undefined as number | undefined,
    idType: "saudi_id" as "saudi_id" | "iqama",
    idNumber: "",
    fullName: "",
    fullNameAr: "",
    birthDate: "",
    nationality: "",
    gender: "male" as "male" | "female",
    bloodType: "",
    mobile: "",
    email: "",
    profession: "",
    idIssueDate: "",
    idIssuePlace: "",
    idExpiryDate: "",
    photoUrl: "",
    idDocumentUrl: "",
    contractUrl: "",
    accessCountryId: undefined as number | undefined,
    accessSiteId: undefined as number | undefined,
    accessZoneId: undefined as number | undefined,
    accessAreaId: undefined as number | undefined,
  });

  // Fetch companies
  const { data: companies } = trpc.mcm.companies.list.useQuery({ isActive: true });

  // Fetch countries, sites (for cascading dropdowns)
  const { data: countries } = trpc.masterData.getCountries.useQuery();
  const { data: allSites } = trpc.sites.getAll.useQuery();
  const { data: allZones } = trpc.zones.getAll.useQuery({});
  const { data: allAreas } = trpc.areas.getAll.useQuery({});

  // Create request mutation
  const createMutation = trpc.mcm.requests.createCardRequest.useMutation({
    onSuccess: () => {
      toast.success(t("mcm.requestCreated", "Card request created successfully"));
      navigate(`/mcm`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const idInputRef = useRef<HTMLInputElement>(null);
  const contractInputRef = useRef<HTMLInputElement>(null);

  const uploadDocMutation = trpc.mcm.uploadDocument.useMutation();

  const handleDocUpload = async (
    file: File,
    docType: "photo" | "id_document" | "contract",
    fieldName: "photoUrl" | "idDocumentUrl" | "contractUrl"
  ) => {
    if (!file) return;
    const maxSize = docType === "photo" ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(t("mcm.fileTooLarge", "File too large. Max size is {{size}}.", { size: docType === "photo" ? "5MB" : "10MB" }));
      return;
    }
    setUploadingDoc(docType);
    try {
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await uploadDocMutation.mutateAsync({
        fileName: file.name,
        mimeType: file.type,
        base64Data,
        docType,
      });
      updateField(fieldName, result.url);
      toast.success(t("mcm.documentUploaded", "Document uploaded successfully"));
    } catch (err: any) {
      toast.error(err?.message || t("mcm.uploadFailed", "Upload failed"));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = (asDraft: boolean = false) => {
    if (!formData.companyType || !formData.idNumber || !formData.fullName) {
      toast.error(t("mcm.fillRequiredFields", "Please fill all required fields"));
      return;
    }
    if (!formData.companyType) {
      toast.error(t("mcm.selectCompanyType", "Please select a company type"));
      return;
    }
    createMutation.mutate({
      companyType: formData.companyType as "contractor" | "subcontractor" | "client" | "permit3" | "internal",
      companyId: formData.companyId || undefined,
      idType: formData.idType,
      idNumber: formData.idNumber,
      fullName: formData.fullName,
      fullNameAr: formData.fullNameAr || undefined,
      birthDate: formData.birthDate,
      nationality: formData.nationality || undefined,
      gender: formData.gender,
      bloodType: formData.bloodType || undefined,
      mobile: formData.mobile,
      email: formData.email || undefined,
      profession: formData.profession || undefined,
      idIssueDate: formData.idIssueDate || undefined,
      idIssuePlace: formData.idIssuePlace || undefined,
      idExpiryDate: formData.idExpiryDate,
      photoUrl: formData.photoUrl || undefined,
      idDocumentUrl: formData.idDocumentUrl || undefined,
      contractUrl: formData.contractUrl || undefined,
      accessLevels: (formData.accessSiteId || formData.accessCountryId) ? [{
        countryId: formData.accessCountryId,
        siteId: formData.accessSiteId,
        zoneId: formData.accessZoneId,
        areaId: formData.accessAreaId,
      }] : [],
    });
  };

  const currentSectionIndex = SECTIONS.findIndex(s => s.code === currentSection);

  const goToNextSection = () => {
    if (currentSectionIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentSectionIndex + 1].code);
    }
  };

  const goToPrevSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSection(SECTIONS[currentSectionIndex - 1].code);
    }
  };

  // Render section content
  const renderSectionContent = () => {
    switch (currentSection) {
      case "company":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.companyType", "Company Type")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Select value={formData.companyType} onValueChange={(v) => updateField("companyType", v)}>
                  <SelectTrigger className="h-10 border-[#E0E0E0]">
                    <SelectValue placeholder={t("mcm.selectCompanyType", "Select company type")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">{t("mcm.internalUser", "Internal User")}</SelectItem>
                    <SelectItem value="contractor">{t("mcm.contractor", "Contractor")}</SelectItem>
                    <SelectItem value="subcontractor">{t("mcm.subcontractor", "Subcontractor")}</SelectItem>
                    <SelectItem value="client">{t("mcm.client", "Client")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.company", "Company")}
                </Label>
                <Select
                  value={formData.companyId?.toString()}
                  onValueChange={(v) => updateField("companyId", parseInt(v))}
                >
                  <SelectTrigger className="h-10 border-[#E0E0E0]">
                    <SelectValue placeholder={t("mcm.selectCompany", "Select company")} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies?.filter((c: any) => c.type === formData.companyType || !formData.companyType)
                      .map((company: any) => (
                        <SelectItem key={company.id} value={company.id.toString()}>
                          {company.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case "personal":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.idType", "ID Type")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Select value={formData.idType} onValueChange={(v) => updateField("idType", v)}>
                  <SelectTrigger className="h-10 border-[#E0E0E0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saudi_id">{t("mcm.saudiId", "Saudi ID")}</SelectItem>
                    <SelectItem value="iqama">{t("mcm.iqama", "Iqama")}</SelectItem>
                    <SelectItem value="passport">{t("mcm.passport", "Passport")}</SelectItem>
                    <SelectItem value="gcc_id">{t("mcm.gccId", "GCC ID")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.idNumber", "ID Number")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Input
                  value={formData.idNumber}
                  onChange={(e) => updateField("idNumber", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  placeholder={t("mcm.enterIdNumber", "Enter ID number")}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.idExpiryDate", "ID Expiry Date")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Input
                  type="date"
                  value={formData.idExpiryDate}
                  onChange={(e) => updateField("idExpiryDate", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.fullName", "Full Name (English)")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Input
                  value={formData.fullName}
                  onChange={(e) => updateField("fullName", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  placeholder={t("mcm.enterFullName", "Enter full name")}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.fullNameAr", "Full Name (Arabic)")}
                </Label>
                <Input
                  value={formData.fullNameAr}
                  onChange={(e) => updateField("fullNameAr", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  dir="rtl"
                  placeholder={t("mcm.enterFullNameAr", "أدخل الاسم الكامل")}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.birthDate", "Date of Birth")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Input
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => updateField("birthDate", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.nationality", "Nationality")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <CountrySelect
                  value={formData.nationality}
                  onChange={(v) => updateField("nationality", v)}
                  options={countries?.map((c: any) => c.name) ?? []}
                  placeholder={t("mcm.enterNationality", "Select nationality")}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.gender", "Gender")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Select value={formData.gender} onValueChange={(v) => updateField("gender", v)}>
                  <SelectTrigger className="h-10 border-[#E0E0E0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t("mcm.male", "Male")}</SelectItem>
                    <SelectItem value="female">{t("mcm.female", "Female")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.mobile", "Mobile Number")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => updateField("mobile", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  placeholder="+966 5XX XXX XXXX"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.email", "Email")}
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  placeholder="email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.profession", "Profession")}
                </Label>
                <Input
                  value={formData.profession}
                  onChange={(e) => updateField("profession", e.target.value)}
                  className="h-10 border-[#E0E0E0]"
                  placeholder={t("mcm.enterProfession", "Enter profession")}
                />
              </div>
            </div>
          </div>
        );

      case "documents":
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Photo upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.photo", "Photo")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <div
                  onClick={() => photoInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                    formData.photoUrl ? "border-[#5B2C93] bg-[#F5F0FF]" : "border-[#E0E0E0] hover:border-[#5B2C93]"
                  )}
                >
                  {uploadingDoc === "photo" ? (
                    <><Loader2 className="h-8 w-8 mx-auto text-[#5B2C93] mb-2 animate-spin" /><p className="text-sm text-[#5B2C93]">{t("mcm.uploading", "Uploading...")}</p></>
                  ) : formData.photoUrl ? (
                    <><img src={formData.photoUrl} alt="Photo" className="h-16 w-16 object-cover rounded mx-auto mb-2" />
                    <p className="text-xs text-[#5B2C93] font-medium">✓ {t("mcm.photoUploaded", "Photo uploaded")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">{t("mcm.clickToReplace", "Click to replace")}</p></>
                  ) : (
                    <><Upload className="h-8 w-8 mx-auto text-[#9CA3AF] mb-2" />
                    <p className="text-sm text-[#6B6B6B]">{t("mcm.uploadPhoto", "Click to upload photo")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PNG, JPG {t("mcm.upTo5MB", "up to 5MB")}</p></>
                  )}
                </div>
              </div>

              {/* ID Document upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.idDocument", "ID Document")} <span className="text-[#FF6B6B]">*</span>
                </Label>
                <div
                  onClick={() => idInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                    formData.idDocumentUrl ? "border-[#5B2C93] bg-[#F5F0FF]" : "border-[#E0E0E0] hover:border-[#5B2C93]"
                  )}
                >
                  {uploadingDoc === "id_document" ? (
                    <><Loader2 className="h-8 w-8 mx-auto text-[#5B2C93] mb-2 animate-spin" /><p className="text-sm text-[#5B2C93]">{t("mcm.uploading", "Uploading...")}</p></>
                  ) : formData.idDocumentUrl ? (
                    <><FileText className="h-8 w-8 mx-auto text-[#5B2C93] mb-2" />
                    <p className="text-xs text-[#5B2C93] font-medium">✓ {t("mcm.idDocumentUploaded", "ID document uploaded")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">{t("mcm.clickToReplace", "Click to replace")}</p></>
                  ) : (
                    <><Upload className="h-8 w-8 mx-auto text-[#9CA3AF] mb-2" />
                    <p className="text-sm text-[#6B6B6B]">{t("mcm.uploadIdDocument", "Click to upload ID document")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PDF, PNG, JPG {t("mcm.upTo10MB", "up to 10MB")}</p></>
                  )}
                </div>
              </div>

              {/* Contract upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.contract", "Contract/Letter")}
                </Label>
                <div
                  onClick={() => contractInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                    formData.contractUrl ? "border-[#5B2C93] bg-[#F5F0FF]" : "border-[#E0E0E0] hover:border-[#5B2C93]"
                  )}
                >
                  {uploadingDoc === "contract" ? (
                    <><Loader2 className="h-8 w-8 mx-auto text-[#5B2C93] mb-2 animate-spin" /><p className="text-sm text-[#5B2C93]">{t("mcm.uploading", "Uploading...")}</p></>
                  ) : formData.contractUrl ? (
                    <><FileText className="h-8 w-8 mx-auto text-[#5B2C93] mb-2" />
                    <p className="text-xs text-[#5B2C93] font-medium">✓ {t("mcm.contractUploaded", "Contract uploaded")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">{t("mcm.clickToReplace", "Click to replace")}</p></>
                  ) : (
                    <><Upload className="h-8 w-8 mx-auto text-[#9CA3AF] mb-2" />
                    <p className="text-sm text-[#6B6B6B]">{t("mcm.uploadContract", "Click to upload contract")}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">PDF {t("mcm.upTo10MB", "up to 10MB")}</p></>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case "access": {
        const sitesForCountry = formData.accessCountryId
          ? (allSites ?? []).filter((s: any) => s.countryId === formData.accessCountryId)
          : (allSites ?? []);
        const zonesForSite = formData.accessSiteId
          ? (allZones ?? []).filter((z: any) => z.siteId === formData.accessSiteId)
          : [];
        const areasForZone = formData.accessZoneId
          ? (allAreas ?? []).filter((a: any) => a.zoneId === formData.accessZoneId)
          : [];
        return (
          <div className="space-y-6">
            <h3 className="font-medium text-[#2C2C2C]">
              {t("mcm.accessLevel", "Access Level")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Country */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.country", "Country")}
                </Label>
                <Select
                  value={formData.accessCountryId?.toString() ?? ""}
                  onValueChange={(v) => {
                    updateField("accessCountryId", parseInt(v));
                    updateField("accessSiteId", undefined);
                    updateField("accessZoneId", undefined);
                    updateField("accessAreaId", undefined);
                  }}
                >
                  <SelectTrigger className="h-10 border-[#E0E0E0] bg-white">
                    <SelectValue placeholder={t("mcm.selectCountry", "Select country")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(countries ?? []).map((c: any) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Site */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.site", "Site")}
                </Label>
                <Select
                  value={formData.accessSiteId?.toString() ?? ""}
                  onValueChange={(v) => {
                    updateField("accessSiteId", parseInt(v));
                    updateField("accessZoneId", undefined);
                    updateField("accessAreaId", undefined);
                  }}
                  disabled={sitesForCountry.length === 0}
                >
                  <SelectTrigger className="h-10 border-[#E0E0E0] bg-white">
                    <SelectValue placeholder={t("mcm.selectSite", "Select site")} />
                  </SelectTrigger>
                  <SelectContent>
                    {sitesForCountry.map((site: any) => (
                      <SelectItem key={site.id} value={site.id.toString()}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Zone */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.zone", "Zone")}
                </Label>
                <Select
                  value={formData.accessZoneId?.toString() ?? ""}
                  onValueChange={(v) => {
                    updateField("accessZoneId", parseInt(v));
                    updateField("accessAreaId", undefined);
                  }}
                  disabled={!formData.accessSiteId || zonesForSite.length === 0}
                >
                  <SelectTrigger className="h-10 border-[#E0E0E0] bg-white">
                    <SelectValue placeholder={!formData.accessSiteId ? t("mcm.selectSiteFirst", "Select site first") : t("mcm.selectZone", "Select zone")} />
                  </SelectTrigger>
                  <SelectContent>
                    {zonesForSite.map((zone: any) => (
                      <SelectItem key={zone.id} value={zone.id.toString()}>{zone.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Area */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2C]">
                  {t("mcm.area", "Area")}
                </Label>
                <Select
                  value={formData.accessAreaId?.toString() ?? ""}
                  onValueChange={(v) => updateField("accessAreaId", parseInt(v))}
                  disabled={!formData.accessZoneId || areasForZone.length === 0}
                >
                  <SelectTrigger className="h-10 border-[#E0E0E0] bg-white">
                    <SelectValue placeholder={!formData.accessZoneId ? t("mcm.selectZoneFirst", "Select zone first") : t("mcm.selectArea", "Select area")} />
                  </SelectTrigger>
                  <SelectContent>
                    {areasForZone.map((area: any) => (
                      <SelectItem key={area.id} value={area.id.toString()}>{area.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      }

      case "review": {
        const reviewCompany = companies?.find((c: any) => c.id === formData.companyId);
        const reviewCountry = (countries ?? []).find((c: any) => c.id === formData.accessCountryId);
        const reviewSite = allSites?.find((s: any) => s.id === formData.accessSiteId);
        const reviewZone = allZones?.find((z: any) => z.id === formData.accessZoneId);
        const reviewArea = allAreas?.find((a: any) => a.id === formData.accessAreaId);

        const ReviewField = ({ label, value }: { label: string; value?: string | null }) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-medium">{label}</span>
            <span className="text-sm font-medium text-[#2C2C2C]">{value || <span className="text-[#C0C0C0] font-normal italic">{t("mcm.notProvided", "Not provided")}</span>}</span>
          </div>
        );

        return (
          <div className="space-y-5">
            {/* Header Banner */}
            <div className="flex items-start gap-3 bg-[#F0EBF9] border border-[#C4A8E8] rounded-lg px-4 py-3">
              <CheckCircle className="h-5 w-5 text-[#5B2C93] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#5B2C93]">{t("mcm.readyToSubmit", "Ready to Submit")}</p>
                <p className="text-xs text-[#7C4DB5] mt-0.5">{t("mcm.reviewBeforeSubmit", "Review all details below before submitting. You can go back to any step to make changes.")}</p>
              </div>
            </div>

            {/* Section 1: Company + Personal side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Company Information */}
              <div className="border border-[#E8E0F0] rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 bg-[#F5F0FF] px-4 py-2.5 border-b border-[#E8E0F0]">
                  <Building2 className="h-4 w-4 text-[#5B2C93]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#5B2C93]">{t("mcm.sections.company", "Company Information")}</span>
                </div>
                <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <ReviewField label={t("mcm.companyType", "Company Type")} value={formData.companyType ? formData.companyType.charAt(0).toUpperCase() + formData.companyType.slice(1) : undefined} />
                  <ReviewField label={t("mcm.company", "Company")} value={reviewCompany?.name} />
                </div>
              </div>

              {/* Personal Information */}
              <div className="border border-[#E8E0F0] rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 bg-[#F5F0FF] px-4 py-2.5 border-b border-[#E8E0F0]">
                  <User className="h-4 w-4 text-[#5B2C93]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#5B2C93]">{t("mcm.sections.personal", "Personal Information")}</span>
                </div>
                <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <ReviewField label={t("mcm.review.fullNameEn", "Full Name (EN)")} value={formData.fullName} />
                  <ReviewField label={t("mcm.review.fullNameAr", "Full Name (AR)")} value={formData.fullNameAr} />
                  <ReviewField label={t("mcm.idType", "ID Type")} value={formData.idType?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} />
                  <ReviewField label={t("mcm.idNumber", "ID Number")} value={formData.idNumber} />
                  <ReviewField label={t("mcm.review.idExpiry", "ID Expiry")} value={formData.idExpiryDate} />
                  <ReviewField label={t("mcm.birthDate", "Date of Birth")} value={formData.birthDate} />
                  <ReviewField label={t("mcm.nationality", "Nationality")} value={formData.nationality} />
                  <ReviewField label={t("mcm.gender", "Gender")} value={formData.gender ? formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1) : undefined} />
                  <ReviewField label={t("mcm.mobile", "Mobile")} value={formData.mobile} />
                  <ReviewField label={t("mcm.email", "Email")} value={formData.email} />
                  <ReviewField label={t("mcm.profession", "Profession")} value={formData.profession} />
                </div>
              </div>
            </div>

            {/* Section 2: Documents + Access Level side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Documents */}
              <div className="border border-[#E8E0F0] rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 bg-[#F5F0FF] px-4 py-2.5 border-b border-[#E8E0F0]">
                  <FileText className="h-4 w-4 text-[#5B2C93]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#5B2C93]">{t("mcm.sections.documents", "Documents")}</span>
                </div>
                <div className="px-4 py-4 space-y-3">
                  {/* Photo */}
                  <div className="flex items-center gap-3">
                    {formData.photoUrl ? (
                      <img src={formData.photoUrl} alt="Photo" className="h-12 w-12 object-cover rounded-lg border border-[#E0E0E0]" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg border-2 border-dashed border-[#E0E0E0] flex items-center justify-center">
                        <User className="h-5 w-5 text-[#C0C0C0]" />
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-medium text-[#2C2C2C]">{t("mcm.photo", "Photo")}</p>
                      <p className="text-xs text-[#6B6B6B]">{formData.photoUrl ? <span className="text-[#059669]">✓ {t("mcm.uploaded", "Uploaded")}</span> : <span className="text-[#F59E0B]">{t("mcm.notUploaded", "Not uploaded")}</span>}</p>
                    </div>
                  </div>
                  {/* ID Document */}
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg border border-[#E0E0E0] flex items-center justify-center bg-[#FAFAFA]">
                      <FileText className="h-5 w-5 text-[#6B6B6B]" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#2C2C2C]">{t("mcm.idDocument", "ID Document")}</p>
                      <p className="text-xs text-[#6B6B6B]">{formData.idDocumentUrl ? <span className="text-[#059669]">✓ {t("mcm.uploaded", "Uploaded")}</span> : <span className="text-[#F59E0B]">{t("mcm.notUploaded", "Not uploaded")}</span>}</p>
                    </div>
                  </div>
                  {/* Contract */}
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg border border-[#E0E0E0] flex items-center justify-center bg-[#FAFAFA]">
                      <FileText className="h-5 w-5 text-[#6B6B6B]" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#2C2C2C]">{t("mcm.contract", "Contract / Letter")}</p>
                      <p className="text-xs text-[#6B6B6B]">{formData.contractUrl ? <span className="text-[#059669]">✓ {t("mcm.uploaded", "Uploaded")}</span> : <span className="text-[#9CA3AF]">{t("mcm.optionalNotUploaded", "Optional — not uploaded")}</span>}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Access Level */}
              <div className="border border-[#E8E0F0] rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 bg-[#F5F0FF] px-4 py-2.5 border-b border-[#E8E0F0]">
                  <Shield className="h-4 w-4 text-[#5B2C93]" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#5B2C93]">{t("mcm.sections.access", "Access Level")}</span>
                </div>
                <div className="px-4 py-4">
                  {formData.accessSiteId || formData.accessCountryId ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        <ReviewField label={t("mcm.country", "Country")} value={reviewCountry?.name} />
                        <ReviewField label={t("mcm.site", "Site")} value={reviewSite?.name} />
                        <ReviewField label={t("mcm.zone", "Zone")} value={reviewZone?.name} />
                        <ReviewField label={t("mcm.area", "Area")} value={reviewArea?.name} />
                      </div>
                      {/* Visual breadcrumb */}
                      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                        {reviewCountry && <span className="px-2 py-1 bg-[#E8DCF5] text-[#5B2C93] text-xs rounded font-medium">{reviewCountry.name}</span>}
                        {reviewCountry && reviewSite && <span className="text-[#C0C0C0] text-xs">›</span>}
                        {reviewSite && <span className="px-2 py-1 bg-[#E8DCF5] text-[#5B2C93] text-xs rounded font-medium">{reviewSite.name}</span>}
                        {reviewSite && reviewZone && <span className="text-[#C0C0C0] text-xs">›</span>}
                        {reviewZone && <span className="px-2 py-1 bg-[#E8DCF5] text-[#5B2C93] text-xs rounded font-medium">{reviewZone.name}</span>}
                        {reviewZone && reviewArea && <span className="text-[#C0C0C0] text-xs">›</span>}
                        {reviewArea && <span className="px-2 py-1 bg-[#E8DCF5] text-[#5B2C93] text-xs rounded font-medium">{reviewArea.name}</span>}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <Shield className="h-8 w-8 text-[#E0E0E0] mb-2" />
                      <p className="text-sm text-[#9CA3AF]">{t("mcm.noAccessLevel", "No access level selected")}</p>
                      <button onClick={() => setCurrentSection("access")} className="text-xs text-[#5B2C93] underline mt-1">{t("mcm.goBackSetAccess", "Go back to set access level")}</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]">
      {/* Header Toolbar */}
      <FioriPageHeader
        title={t("mcm.createNewCardRequest", "Create New Card Request")}
        subtitle={t("mcm.fillDetails", "Fill in the card request details")}
        icon={<CreditCard className="h-5 w-5" />}
        onBack={() => {}}
      />
      <div className="bg-white border-b px-3 sm:px-6 py-2 sm:py-3 flex flex-wrap items-center gap-2 sm:gap-4">
        <div className="hidden sm:block h-6 w-px bg-[#E0E0E0] ml-2" />

        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-6 text-[#6B6B6B]">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase font-medium text-[#9CA3AF]">
              {t("mcm.requestType", "Request Type")}
            </span>
            <Badge
              variant="outline"
              className="bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93] rounded-sm px-2 py-0.5"
            >
              <CreditCard className="h-3 w-3 mr-1" />
              {t("mcm.newCard", "NEW CARD")}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase font-medium text-[#9CA3AF]">
              {t("common.status", "Status")}
            </span>
            <Badge
              variant="outline"
              className="bg-[#E8DCF5] text-[#5B2C93] border-[#5B2C93] rounded-sm px-2 py-0.5"
            >
              {t("common.new", "NEW")}
            </Badge>
          </div>
        </div>
      </div>

      {/* Always-mounted hidden file inputs for document uploads */}
      <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDocUpload(f, "photo", "photoUrl"); e.target.value = ""; }} />
      <input ref={idInputRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDocUpload(f, "id_document", "idDocumentUrl"); e.target.value = ""; }} />
      <input ref={contractInputRef} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDocUpload(f, "contract", "contractUrl"); e.target.value = ""; }} />

      {/* Mobile Step Indicator */}
      <div className="xl:hidden bg-white border-b overflow-x-auto shrink-0">
        <div className="flex items-stretch min-w-max">
          {SECTIONS.map((section, index) => {
            const isActive = currentSection === section.code;
            const isCompleted = index < currentSectionIndex;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setCurrentSection(section.code)}
                className={cn(
                  "flex flex-col items-center gap-1 px-3 py-2 text-xs font-medium transition-colors border-b-2 shrink-0",
                  isActive
                    ? "border-[#5B2C93] text-[#5B2C93] bg-[#F5F0FF]"
                    : "border-transparent text-[#6B6B6B] hover:bg-[#F5F5F5]"
                )}
              >
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold",
                  isActive ? "bg-[#5B2C93] text-white"
                    : isCompleted ? "bg-[#D1FAE5] text-[#059669]"
                    : "bg-[#F0F0F0] text-[#6B6B6B]"
                )}>
                  {isCompleted ? <CheckCircle className="h-3 w-3" /> : index + 1}
                </div>
                <span className="whitespace-nowrap text-[10px]">{section.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col xl:flex-row">
        {/* Left Navigation Sidebar */}
        <div className="hidden xl:flex w-64 bg-white border-r flex-col overflow-y-auto shrink-0">
          <div className="p-4 border-b">
            <p className="text-sm font-medium text-[#2C2C2C] tracking-wider">{t("mcm.steps", "Steps")}</p>
          </div>
          <nav className="flex-1 p-2">
            {SECTIONS.map((section, index) => {
              const isActive = currentSection === section.code;
              const isCompleted = index < currentSectionIndex;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setCurrentSection(section.code)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors mb-1",
                    isActive
                      ? "bg-[#E8DCF5] text-[#2C2C2C] border-l-4 border-[#5B2C93]"
                      : "text-[#2C2C2C] hover:bg-[#F5F5F5]"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0",
                    isActive ? "bg-[#5B2C93] text-white"
                      : isCompleted ? "bg-[#D1FAE5] text-[#059669]" : "bg-[#F5F5F5] text-[#6B6B6B]"
                  )}>
                    {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{section.name}</p>
                  </div>
                  {isActive && <svg className="h-4 w-4 text-[#5B2C93] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Section Header */}
          <div className="bg-white border-b px-4 sm:px-6 py-3 sm:py-4 shrink-0">
            <h2 className="text-base sm:text-lg font-medium text-[#2C2C2C]">
              {SECTIONS.find(s => s.code === currentSection)?.name}
            </h2>
          </div>

          {/* Section Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-[#F5F5F5]">
            {renderSectionContent()}
          </div>

          {/* Footer Navigation */}
          <div className="bg-white border-t px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0">
            <Button
              variant="ghost"
              onClick={goToPrevSection}
              disabled={currentSectionIndex === 0}
              className="text-[#6B6B6B]"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("common.previous", "Previous")}
            </Button>
            <div className="flex gap-2 sm:gap-3">
              {currentSection === "review" ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleSubmit(true)}
                    disabled={createMutation.isPending}
                    size="sm"
                  >
                    <Save className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t("common.saveAsDraft", "Save as Draft")}</span>
                  </Button>
                  <Button
                    onClick={() => handleSubmit(false)}
                    disabled={createMutation.isPending}
                    className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2"
                    size="sm"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t("common.submit", "Submit Request")}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={goToNextSection}
                  className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2"
                  size="sm"
                >
                  {t("common.next", "Next")}
                  <ArrowLeft className="h-4 w-4 rotate-180" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
